import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { IRequestRecord } from 'types/requests.interface'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { Box, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { ISelectValue } from 'constants/globalTypes'
import { Input } from 'components/Input/Input'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { tokens } from 'styling/tokens'
import { formatDateForDisplay } from 'utils/helpers'
import Avatar from '../../../assets/images/profileAvatar.png'
import { SearchField } from '../../Accomodation/Accommodation.style'
import {
  requestTypeOptions,
  typeLabel,
  statusLabel,
  statusPill,
  typePill,
  durationLabel,
  ST_PENDING,
  ST_APPROVED,
  ST_REJECTED,
} from '../requestsData'
import {
  StatBand,
  FilterBar,
  StatusTabs,
  PersonCell,
  PersonAvatar,
  PersonText,
  PersonName,
  PersonMeta,
  DateCell,
  DurationCell,
  RowActions,
  QuickBtn,
} from './RequestsBoard.styled'

const CheckIcon = () => (
  <svg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M3 8.5l3.2 3.2L13 4.8' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

const dateRange = (req: IRequestRecord) => {
  const from = formatDateForDisplay(req.from)
  if (req.from.slice(0, 10) === req.to.slice(0, 10)) return from
  return `${from} – ${formatDateForDisplay(req.to)}`
}

export const RequestsBoard = ({
  scope,
  onSelect,
  onQuickApprove,
  onCancel,
}: {
  scope: 'approvals' | 'mine'
  onSelect: (req: IRequestRecord) => void
  onQuickApprove: (req: IRequestRecord) => void
  onCancel: (req: IRequestRecord) => void
}) => {
  const { requests, currentUser } = useSelector((state: RootState) => state.requests)
  const [status, setStatus] = useState<number | null>(scope === 'approvals' ? ST_PENDING : null)
  const [type, setType] = useState<number | null>(null)
  const [search, setSearch] = useState('')

  const scoped = useMemo(() => {
    if (scope === 'mine') {
      return requests.filter((r) => r.requester.id === currentUser.id)
    }
    return requests
  }, [requests, scope, currentUser.id])

  const counts = useMemo(
    () => ({
      pending: scoped.filter((r) => r.status === ST_PENDING).length,
      approved: scoped.filter((r) => r.status === ST_APPROVED).length,
      rejected: scoped.filter((r) => r.status === ST_REJECTED).length,
    }),
    [scoped],
  )

  const rows = useMemo(() => {
    let list = scoped
    if (status !== null) list = list.filter((r) => r.status === status)
    if (type !== null) list = list.filter((r) => r.type === type)
    const q = search.trim().toLowerCase()
    if (q) list = list.filter((r) => r.requester.name.toLowerCase().includes(q))
    return [...list].sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
  }, [scoped, status, type, search])

  const statusTabs = [
    { value: ST_PENDING, label: `Pending (${counts.pending})` },
    { value: ST_APPROVED, label: `Approved (${counts.approved})` },
    { value: ST_REJECTED, label: `Rejected (${counts.rejected})` },
    { value: null as number | null, label: `All (${scoped.length})` },
  ]

  const columns: IHonestTableColumn<IRequestRecord>[] = [
    {
      id: 'person',
      header: 'Employee',
      minWidth: '13rem',
      cell: (r) => (
        <PersonCell>
          <PersonAvatar>
            <img src={r.requester.avatarUrl || Avatar} alt='' onError={({ currentTarget }) => (currentTarget.src = Avatar)} />
          </PersonAvatar>
          <PersonText>
            <PersonName>{r.requester.name}</PersonName>
            <PersonMeta>
              {r.requester.jobTitle} · {r.requester.site}
            </PersonMeta>
          </PersonText>
        </PersonCell>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      minWidth: '8rem',
      cell: (r) => <StatusPill status={typePill(r.type)}>{typeLabel(r.type)}</StatusPill>,
    },
    {
      id: 'dates',
      header: 'Dates',
      fill: true,
      minWidth: '11rem',
      cell: (r) => <DateCell>{dateRange(r)}</DateCell>,
    },
    {
      id: 'duration',
      header: 'Duration',
      minWidth: '6rem',
      cell: (r) => <DurationCell>{durationLabel(r)}</DurationCell>,
    },
    {
      id: 'status',
      header: 'Status',
      minWidth: '7rem',
      cell: (r) => (
        <StatusPill status={statusPill(r.status)} dot>
          {statusLabel(r.status)}
        </StatusPill>
      ),
    },
    {
      id: 'act',
      header: '',
      align: 'right',
      minWidth: scope === 'approvals' ? '6rem' : '7rem',
      cell: (r) =>
        r.status !== ST_PENDING ? (
          <span />
        ) : scope === 'approvals' ? (
          <RowActions onClick={(e) => e.stopPropagation()}>
            <QuickBtn $variant='approve' type='button' aria-label='Approve' onClick={() => onQuickApprove(r)}>
              <CheckIcon />
            </QuickBtn>
            <QuickBtn $variant='reject' type='button' aria-label='Reject' onClick={() => onSelect(r)}>
              <CloseIcon />
            </QuickBtn>
          </RowActions>
        ) : (
          <RowActions onClick={(e) => e.stopPropagation()}>
            <Button variant='secondary' size='sm' onClick={() => onCancel(r)}>
              Cancel
            </Button>
          </RowActions>
        ),
    },
  ]

  return (
    <>
      <StatBand>
        <MetricCard layout='inline' label='Pending' value={String(counts.pending)} accentColor={tokens.status.warning.fg} />
        <MetricCard layout='inline' label='Approved' value={String(counts.approved)} accentColor={tokens.status.success.fg} />
        <MetricCard layout='inline' label='Rejected' value={String(counts.rejected)} accentColor={tokens.status.danger.fg} />
      </StatBand>

      <FilterBar>
        <StatusTabs>
          <TabsContainer>
            {statusTabs.map((t) => (
              <Tabs key={String(t.value)} active={status === t.value} onClick={() => setStatus(t.value)}>
                {t.label}
              </Tabs>
            ))}
          </TabsContainer>
        </StatusTabs>
        <HonestSelectInput
          width='200px'
          nomarg
          labelText='Type'
          options={requestTypeOptions}
          defaultValue={type}
          onChange={(v: ISelectValue) => setType(v ? Number(v.value) : null)}
        />
        <SearchField>
          <Input
            search
            nomarg
            width='240px'
            labelText='Search'
            placeholder='Search by name'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            rightIcon={search ? <CloseIcon /> : null}
            onRightIconClick={search ? () => setSearch('') : undefined}
          />
        </SearchField>
      </FilterBar>

      <Box>
        <HonestTable
          columns={columns}
          data={rows}
          keyField='id'
          onClick={onSelect}
          emptyMessage={scope === 'approvals' ? 'Nothing to review' : 'No requests yet'}
        />
      </Box>
    </>
  )
}
